import React, { useContext, useEffect } from 'react';
import { logClientError } from './error-logger.js';
import { getCookieValue } from './cookie.utils.js';
import { CookieEnum } from '../hooks/cookie.hook.js';
import { Bucket, Environment } from '@at/consumer-web-types';
import { CookiePolicy, NotFoundError, Platform, RedirectError } from '../types.js';
const bucketCookie = (defaultBucket = Bucket.premiummobile) => (getCookieValue(CookieEnum.PLATFORM_COOKIE_NAME) || defaultBucket);
const cookiePolicyCookie = () => (getCookieValue(CookieEnum.COOKIE_POLICY_NAME) || 'none');
const abTestGroupsCookie = () => {
    const testGroups = getCookieValue(CookieEnum.AB_TEST_GROUPS_NAME);
    if (testGroups && testGroups.length) {
        return testGroups.split('-');
    }
    if (typeof window !== 'undefined' && window.abTestGroups) {
        return window.abTestGroups.split('-');
    }
    return [];
};
const loadDefault = (ssrMode, defaultBucket) => {
    const config = (typeof window !== 'undefined' && window.AT_SPA_JS_CONFIG) || {};
    return {
        config: Object.assign({ appName: '', appVersion: '', env: Environment.local }, config),
        helmetContext: {},
        ssrMode,
        abTestGroups: !ssrMode ? abTestGroupsCookie() : [],
        bucket: !ssrMode ? bucketCookie(defaultBucket) : defaultBucket,
        cookiePolicy: !ssrMode ? cookiePolicyCookie() : CookiePolicy.none,
        getPlatform: () => Platform.mobile,
        isDesktop: () => false,
        isMobile: () => true,
        matchesTestGroups: () => false,
        redirect: () => {
        },
        notFound: () => {
        },
    };
};
const AppContext = React.createContext({});
export const withAppContext = (WrappedComponent, defaultAppConfig, ssrMode = false, abTestGroups = [], bucket = Bucket.premiummobile, helmetContext = {}) => {
    const defaultState = loadDefault(ssrMode, bucket);
    const mergedConfig = Object.assign(Object.assign({}, defaultState.config), defaultAppConfig);
    const value = Object.assign(Object.assign({}, defaultState), { ssrMode, config: mergedConfig });
    if (ssrMode && abTestGroups.length) {
        value.abTestGroups = abTestGroups;
    }
    value.getPlatform = () => {
        return !value.bucket || value.bucket === Bucket.premiummobile ? Platform.mobile : Platform.desktop;
    };
    value.isDesktop = () => {
        return value.getPlatform() === Platform.desktop;
    };
    value.isMobile = () => {
        return value.getPlatform() === Platform.mobile;
    };
    value.matchesTestGroups = (...groups) => {
        for (let i = 0; i < value.abTestGroups.length; i++) {
            const group = value.abTestGroups[i];
            if (groups.some((g) => g === group)) {
                return true;
            }
        }
        return false;
    };
    value.redirect = (url, statusCode) => {
        if (!ssrMode && typeof window !== 'undefined') {
            window.location.href = url;
        }
        else {
            throw new RedirectError(url, statusCode);
        }
    };
    value.notFound = (statusCode = 404, url) => {
        if (!ssrMode && url && typeof window !== 'undefined') {
            window.location.href = url;
        }
        else {
            throw new NotFoundError(statusCode);
        }
    };
    value.helmetContext = helmetContext;
    const WithAppContext = (props) => {
        useEffect(() => {
            if (!ssrMode && typeof window !== 'undefined' && window.navigator) {
                window.onerror = (message, url, lineNumber, columnNumber, error) => {
                    logClientError({
                        appName: value.config.appName,
                        message: `${message}`,
                        url,
                        requestUrl: window.location.href,
                        path: window.location.pathname,
                        query: window.location.search,
                        stack: error && error.stack,
                    });
                };
            }
        });
        return (React.createElement(AppContext.Provider, { value: value },
            React.createElement(WrappedComponent, Object.assign({}, props))));
    };
    return WithAppContext;
};
const useAppContext = () => useContext(AppContext);
export { loadDefault as defaultAppContext, useAppContext };
